import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  ListSubheader,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import {
  ArrowRight,
  CaretDown,
  DotsThree,
  FunnelSimple,
  Plus,
  SealCheck,
  Target as TargetIcon,
  X,
} from "@phosphor-icons/react";

import type { Status as StatusId } from "api/types/aba";
import type { ClinicalPhase } from "../../shared/types";
import type { TargetFilterOption, TargetActiveFilter } from "./useTargetFilters";
import { BLOCK_BORDER_COLOR } from "../../shared/constants";
import StatusPill from "components/ClientDetails/Sections/GoalsAndTargets/shared/StatusPill";
import ClinicalPhasePill from "../../shared/ClinicalPhasePill";
import EmptyState from "../../shared/EmptyState";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface GoalTarget {
  /** Unique identifier */
  id: string;
  /** Display name of the target behavior */
  name: string;
  /** Number of data collection sessions recorded */
  sessionCount: number;
  /** Human-readable date of last session, e.g. "Mar 10" */
  lastSessionDate: string;
  /** Current status of the target */
  status: StatusId;
  /**
   * Current clinical phase — when undefined, the target has not yet been
   * added to a program and shows an "Add to Program" action instead.
   */
  clinicalPhase?: ClinicalPhase;
  /** Optional mastery progress hint, e.g. "2 of 3 sessions approaching mastery" */
  masteryHint?: string;
  /** Whether the target is archived — archived rows render dimmed */
  isArchived?: boolean;
}

export interface GoalTargetsListProps {
  /** Filtered list of targets to display */
  targets: GoalTarget[];
  /** Total count before filtering — drives the "X of Y" label when filters are active */
  totalTargetCount?: number;
  /** Available filter options for the dropdown menu */
  filterOptions?: TargetFilterOption[];
  /** Currently active filter chips */
  activeFilters?: TargetActiveFilter[];
  /** Number of targets that need attention — omit or 0 to hide the button */
  needsAttentionCount?: number;
  /** Whether the "Needs Attention" quick-filter is currently active */
  isNeedsAttentionActive?: boolean;
  /** Called when "Add Target" is clicked */
  onAddTarget?: () => void;
  /** Called when a filter option is selected from the menu */
  onAddFilter?: (option: TargetFilterOption) => void;
  /** Called when a filter chip's delete icon is clicked */
  onDeleteFilter?: (filterId: string) => void;
  /** Called when all filters are cleared */
  onClearFilters?: () => void;
  /** Called when "Needs Attention" is clicked */
  onNeedsAttentionClick?: () => void;
  /** Called when a target's overflow menu button is clicked */
  onTargetMenuOpen?: (event: React.MouseEvent<HTMLButtonElement>, targetId: string) => void;
  /** Called when a target's navigate arrow is clicked */
  onTargetNavigate?: (targetId: string) => void;
  /** Called when "Add to Program" is clicked for a target not yet in a program */
  onAddToProgram?: (targetId: string) => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const GROUP_LABELS: Record<TargetFilterOption["group"], string> = {
  phase: "Phase",
  status: "Status",
};

const FILTER_GROUPS: TargetFilterOption["group"][] = ["phase", "status"];

// ---------------------------------------------------------------------------
// Internal: FiltersMenu
// ---------------------------------------------------------------------------

interface FiltersMenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  filterOptions: TargetFilterOption[];
  activeFilterIds: Set<string>;
  onAddFilter?: (option: TargetFilterOption) => void;
}

const FiltersMenu: React.FC<FiltersMenuProps> = ({
  anchorEl,
  open,
  onClose,
  filterOptions,
  activeFilterIds,
  onAddFilter,
}) => (
  <Menu
    id="target-filters-menu"
    anchorEl={anchorEl}
    open={open}
    onClose={onClose}
    MenuListProps={{ "aria-label": "Filter options", dense: true }}
  >
    {FILTER_GROUPS.flatMap((group, groupIdx) => {
      const groupOptions = filterOptions.filter((o) => o.group === group);
      if (!groupOptions.length) return [];

      const items: React.ReactNode[] = [];

      if (groupIdx > 0) {
        items.push(<Divider key={`divider-${group}`} />);
      }

      items.push(
        <ListSubheader key={`header-${group}`} disableSticky>
          {GROUP_LABELS[group]}
        </ListSubheader>
      );

      groupOptions.forEach((option) => {
        items.push(
          <MenuItem
            key={option.id}
            disabled={activeFilterIds.has(option.id)}
            onClick={() => {
              onAddFilter?.(option);
              onClose();
            }}
          >
            {option.label}
          </MenuItem>
        );
      });

      return items;
    })}
  </Menu>
);

// ---------------------------------------------------------------------------
// Internal: GoalTargetRow
// ---------------------------------------------------------------------------

interface GoalTargetRowProps {
  target: GoalTarget;
  onMenuOpen?: (event: React.MouseEvent<HTMLButtonElement>, targetId: string) => void;
  onNavigate?: (targetId: string) => void;
  onAddToProgram?: (targetId: string) => void;
}

function GoalTargetRow({ target, onMenuOpen, onNavigate, onAddToProgram }: GoalTargetRowProps) {
  const isInProgram = Boolean(target.clinicalPhase);

  return (
    <Box
      component="li"
      aria-label={target.name}
      sx={(theme) => ({
        display: "flex",
        alignItems: "center",
        px: 5,
        py: 3,
        border: `1px solid ${BLOCK_BORDER_COLOR}`,
        borderRadius: "12px",
        bgcolor: "background.paper",
        opacity: target.isArchived ? 0.8 : 1,
        transition: theme.transitions.create("background-color", {
          duration: theme.transitions.duration.shortest,
        }),
        "&:hover": { bgcolor: "action.hover" },
      })}
    >
      {/* Left: name, session info, optional mastery hint */}
      <Stack direction="column" sx={{ flex: 1, minWidth: 0, py: 1 }}>
        <Typography component="span" variant="subtitle2" color="text.primary" noWrap>
          {target.name}
        </Typography>

        <Typography variant="caption" color="text.secondary">
          {target.sessionCount} sessions · Last: {target.lastSessionDate}
        </Typography>

        {target.masteryHint && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 3, mt: 1 }}>
            <SealCheck size={16} weight="duotone" color="#15803D" style={{ flexShrink: 0 }} />
            <Typography
              variant="caption"
              sx={(theme) => ({
                color: (theme.palette as any).green?.[700] ?? "#15803d",
                flex: 1,
              })}
            >
              {target.masteryHint}
            </Typography>
          </Box>
        )}
      </Stack>

      {/* Right: phase + status (or "Add to Program") + action buttons */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
        {isInProgram ? (
          <>
            <ClinicalPhasePill phase={target.clinicalPhase!} />
            <StatusPill status={target.status} />
          </>
        ) : (
          <Button
            variant="contained"
            color="secondary"
            size="small"
            onClick={() => onAddToProgram?.(target.id)}
            aria-label={`Add ${target.name} to program`}
          >
            Add to Program
          </Button>
        )}

        <IconButton
          size="small"
          aria-label={`Options for ${target.name}`}
          onClick={(e) => onMenuOpen?.(e, target.id)}
        >
          <DotsThree size={20} weight="bold" />
        </IconButton>

        <IconButton
          size="small"
          aria-label={`Go to ${target.name}`}
          onClick={() => onNavigate?.(target.id)}
        >
          <ArrowRight weight="light" format="outline" size={16} />
        </IconButton>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// GoalTargetsList
// ---------------------------------------------------------------------------

/**
 * Displays the list of targets associated with a goal in the GoalDetail view.
 * Includes a section header with an "Add Target" action, a self-contained filter
 * toolbar (phase + status dropdown menu + active chips + "Needs Attention" quick-filter
 * + clear), a "X of Y Targets" count, and individual target rows.
 *
 * Filter state lives in the parent via useTargetFilters — this component is
 * purely presentational for the filter UI.
 *
 * Each row adapts based on whether the target is in a program:
 * - In program: shows ClinicalPhasePill + StatusPill + overflow + navigate
 * - Not in program: shows "Add to Program" button + overflow + navigate
 */
const GoalTargetsList: React.FC<GoalTargetsListProps> = ({
  targets,
  totalTargetCount,
  filterOptions = [],
  activeFilters = [],
  needsAttentionCount = 0,
  isNeedsAttentionActive = false,
  onAddTarget,
  onAddFilter,
  onDeleteFilter,
  onClearFilters,
  onNeedsAttentionClick,
  onTargetMenuOpen,
  onTargetNavigate,
  onAddToProgram,
}) => {
  const [filtersAnchor, setFiltersAnchor] = useState<HTMLElement | null>(null);

  const hasActiveFilters = activeFilters.length > 0;
  const hasAnyFilter = hasActiveFilters || isNeedsAttentionActive;
  const activeFilterIds = new Set(activeFilters.map((f) => f.id));

  const total = totalTargetCount ?? targets.length;
  const countLabel = hasAnyFilter
    ? `${targets.length} of ${total} ${total === 1 ? "Target" : "Targets"}`
    : `${targets.length} ${targets.length === 1 ? "Target" : "Targets"}`;

  return (
    <>
      <FiltersMenu
        anchorEl={filtersAnchor}
        open={Boolean(filtersAnchor)}
        onClose={() => setFiltersAnchor(null)}
        filterOptions={filterOptions}
        activeFilterIds={activeFilterIds}
        onAddFilter={onAddFilter}
      />

      <Box
        component="section"
        aria-label="Goal targets"
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 4,
          p: 5,
          bgcolor: "background.paper",
          border: `1px solid ${BLOCK_BORDER_COLOR}`,
          borderRadius: "12px",
          width: "100%",
        }}
      >
        {/* ── Header ── */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 3,
          }}
        >
          <Stack direction="column">
            <Typography variant="body2" color="text.primary">
              Targets
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Individual behaviors being measured for this goal
            </Typography>
          </Stack>

          <Button
            variant="contained"
            color="secondary"
            size="small"
            startIcon={<Plus size={16} />}
            onClick={onAddTarget}
            aria-label="Add new target"
          >
            Add Target
          </Button>
        </Box>

        {/* ── Filter toolbar ── */}
        {total > 0 && (
          <Box
            component="nav"
            aria-label="Target filters"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            {/* Left: Filters button + active chips + Needs Attention */}
            <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 3 }}>
              <Button
                variant="contained"
                color="secondary"
                size="small"
                startIcon={<FunnelSimple size={16} />}
                endIcon={<CaretDown size={14} />}
                onClick={(e) => setFiltersAnchor(e.currentTarget)}
                aria-label="Open filter options"
                aria-haspopup="true"
                aria-expanded={Boolean(filtersAnchor)}
                aria-controls="target-filters-menu"
              >
                Filters
              </Button>

              {hasActiveFilters && (
                <Box
                  component="ul"
                  aria-label="Active filters"
                  sx={{ p: 0, m: 0, display: "flex", flexWrap: "wrap", gap: 3, listStyle: "none" }}
                >
                  {activeFilters.map((filter) => (
                    <Box component="li" key={filter.id}>
                      <Chip
                        label={filter.label}
                        variant="outlined"
                        size="small"
                        onDelete={() => onDeleteFilter?.(filter.id)}
                        deleteIcon={<X size={14} aria-label={`Remove filter: ${filter.label}`} />}
                      />
                    </Box>
                  ))}
                </Box>
              )}

              {needsAttentionCount > 0 && (
                <Button
                  variant={isNeedsAttentionActive ? "contained" : "text"}
                  color="secondary"
                  size="small"
                  onClick={onNeedsAttentionClick}
                  aria-label={`${needsAttentionCount} targets need attention`}
                  aria-pressed={isNeedsAttentionActive}
                >
                  Needs Attention ({needsAttentionCount})
                </Button>
              )}
            </Box>

            {/* Right: target count + clear */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2" color="text.secondary" noWrap>
                {countLabel}
              </Typography>

              {hasAnyFilter && (
                <Button
                  variant="text"
                  color="primary"
                  size="small"
                  startIcon={<X size={14} />}
                  onClick={onClearFilters}
                  aria-label="Clear all filters"
                >
                  Clear
                </Button>
              )}
            </Box>
          </Box>
        )}

        {/* ── Target list / empty state ── */}
        {total === 0 ? (
          <EmptyState
            variant="grey"
            title="Add the first target."
            description="Targets are the specific skills or behaviors you'll measure toward this goal."
            actionPlacement="bottom"
            action={
              <Button
                variant="contained"
                color="secondary"
                size="small"
                startIcon={<TargetIcon size={16} weight="regular" />}
                onClick={onAddTarget}
              >
                Add First Target
              </Button>
            }
          />
        ) : (
          <Stack
            component="ul"
            direction="column"
            gap={4}
            aria-label="Target list"
            sx={{ p: 0, m: 0, listStyle: "none" }}
          >
            {targets.map((target) => (
              <GoalTargetRow
                key={target.id}
                target={target}
                onMenuOpen={onTargetMenuOpen}
                onNavigate={onTargetNavigate}
                onAddToProgram={onAddToProgram}
              />
            ))}
          </Stack>
        )}
      </Box>
    </>
  );
};

export default GoalTargetsList;
